import { isCheckStale, type CheckStatus } from './monitoring';

/**
 * Service health (ARCHITECTURE_CORRECTION_PLAN §H; ADR 0004).
 *
 * Health is derived from stored check results, never typed in and never invented. Two levels of
 * check are kept apart:
 *  - reachability: the host answers (ping/TCP). This alone NEVER makes a service healthy.
 *  - service:      the service itself answers correctly (HTTPS, application status, SMTP/IMAP).
 *
 * States: healthy | needs_attention | unknown (OD-7). A service is `unknown` whenever the evidence
 * is insufficient: no service-level checks, a check that has not run yet, or a stale result.
 */
export const HEALTH_STATES = ['healthy', 'needs_attention', 'unknown'] as const;
export type HealthState = (typeof HEALTH_STATES)[number];

export const HEALTH_REASONS = [
  'no_service_checks',
  'awaiting_first_check',
  'check_stale',
  'check_failing',
  'check_warning',
  'all_service_checks_passing',
] as const;
export type HealthReason = (typeof HEALTH_REASONS)[number];

export type CheckLevel = 'reachability' | 'service';

/** The latest state of one enabled check. */
export interface CheckState {
  level: CheckLevel;
  /** Null when the check has never produced a result. */
  status: CheckStatus | null;
  checkedAt: Date | null;
  stale: boolean;
}

export interface ServiceHealth {
  state: HealthState;
  reason: HealthReason;
  /** The most recent check time considered, if any. */
  checkedAt: Date | null;
}

/** The stored state of one enabled check, as kept on the check row (newest result mirrored). */
export interface CheckRecord {
  intervalSeconds: number;
  lastCheckedAt: Date | null;
  lastStatus: CheckStatus | null;
  /** The check's last configuration change; staleness counts from the later of the two. */
  updatedAt: Date;
}

/**
 * Health input for one enabled check at `now`. The one place the stale rule is applied to a
 * stored check, so the API (health on read) and the worker (alert evaluation) cannot disagree.
 */
export function checkStateOf(record: CheckRecord, now: Date): CheckState {
  return {
    level: 'service',
    status: record.lastStatus,
    checkedAt: record.lastCheckedAt,
    stale: isCheckStale(record.lastCheckedAt, record.intervalSeconds, now, record.updatedAt),
  };
}

export function deriveServiceHealth(checks: readonly CheckState[]): ServiceHealth {
  const checkedAt = checks.reduce<Date | null>(
    (max, c) => (c.checkedAt && (max === null || c.checkedAt > max) ? c.checkedAt : max),
    null,
  );
  const result = (state: HealthState, reason: HealthReason): ServiceHealth => ({
    state,
    reason,
    checkedAt,
  });

  // Only current (non-stale) results count as evidence.
  const current = checks.filter((c) => c.status !== null && !c.stale);
  if (current.some((c) => c.status === 'failed')) return result('needs_attention', 'check_failing');
  if (current.some((c) => c.status === 'warning'))
    return result('needs_attention', 'check_warning');

  // A reachable host proves nothing about the service itself.
  const serviceChecks = checks.filter((c) => c.level === 'service');
  if (serviceChecks.length === 0) return result('unknown', 'no_service_checks');
  if (serviceChecks.some((c) => c.status === null))
    return result('unknown', 'awaiting_first_check');
  if (serviceChecks.some((c) => c.stale)) return result('unknown', 'check_stale');
  return result('healthy', 'all_service_checks_passing');
}
